import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Toolbar,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Autocomplete,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../utils/Api";
import { KeyboardArrowLeft, LocalMallOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const LocalStockListing = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState(null);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [activeProcurement, setActiveProcurement] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API.BASE_URL}vendor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(res?.data?.data?.vendors || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch vendors for filter.");
    }
  };

  const fetchLocalProcurements = async (opts = {}) => {
    setLoading(true);
    const currentPage = opts.page ?? page;
    const currentVendor = "vendor" in opts ? opts.vendor : vendor;
    const currentFromDate = "fromDate" in opts ? opts.fromDate : fromDate;
    const currentToDate = "toDate" in opts ? opts.toDate : toDate;

    try {
      const res = await axios.get(`${API.BASE_URL}local-procurement`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          outlet: outletData?._id,
          page: currentPage,
          limit,
          vendor: currentVendor?._id || currentVendor || undefined,
          startDate: currentFromDate || undefined,
          endDate: currentToDate || undefined,
        },
      });

      const rows = res?.data?.data?.dbResponse || [];
      const totalRecords = rows.length; // keeping your existing behavior
      setList(rows);
      setTotal(totalRecords);
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(
        err?.response?.data?.data?.message || "Failed to fetch local procurements."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchProcurementItems = async (procurementId) => {
    setItemsLoading(true);
    setItems([]);
    try {
      const res = await axios.get(`${API.BASE_URL}local-procurement-item`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { localProcurementId: procurementId },
      });

      const rows = res?.data?.data?.dbResponse || [];
      setItems(rows);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch items for this procurement."
      );
    } finally {
      setItemsLoading(false);
    }
  };

  const handleViewItems = (row) => {
    setActiveProcurement(row);
    setItemsDialogOpen(true);
    fetchProcurementItems(row._id);
  };

  const handleCloseItemsDialog = () => {
    setItemsDialogOpen(false);
    setActiveProcurement(null);
    setItems([]);
  };

  const handleAskDeleteProcurement = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteProcurement = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);

      await axios.delete(`${API.BASE_URL}local-procurement`, {
        data: { _id: deleteTarget._id },
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(
        `Local procurement with Invoice #${deleteTarget.invoiceNo || ""} deleted successfully.`
      );

      setList((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      setTotal((prev) => Math.max(prev - 1, 0));

      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.error || err?.message || "Failed to delete local procurement."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDownloadInvoice = (row) => {
    const url = row.invoice_file;
    if (!url) {
      toast.error("No invoice file found for this procurement.");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = url;

      const baseName = row.invoiceNo ? `invoice-${row.invoiceNo}` : "invoice";
      const lastPart = url.split("/").pop() || "";
      const extMatch = lastPart.match(/\.[^.?]+/);
      const ext = extMatch ? extMatch[0] : "";

      link.download = `${baseName}${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchLocalProcurements({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    const payload = {
      page: 1,
      search,
      vendor,
      fromDate,
      toDate,
    };

    setPage(1);
    fetchLocalProcurements(payload);
  };

  const handleReset = () => {
    setSearch("");
    setVendor(null);
    setFromDate("");
    setToDate("");
    setPage(1);

    fetchLocalProcurements({
      page: 1,
      search: "",
      vendor: null,
      fromDate: "",
      toDate: "",
    });

    toast.info("Filters cleared.");
  };

  const handlePageChange = (direction) => {
    const newPage = page + direction;
    if (newPage < 1) return;

    const maxPage = Math.max(1, Math.ceil((total || list.length) / limit));
    if (newPage > maxPage) return;

    setPage(newPage);
    fetchLocalProcurements({ page: newPage });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-IN");
  };

  const formatMoney = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const computeItemAmount = (item) => {
    if (item.amount !== undefined && item.amount !== null) {
      return Number(item.amount);
    }
    const q = Number(item.quantity || 0);
    const r = Number(item.rate || 0);
    return q * r;
  };

  const totalPages = Math.max(1, Math.ceil((total || list.length) / limit));
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total || list.length);

  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return list;

    return list.filter((row) => {
      const invoiceNo = (row.invoiceNo || row.invoice_no || "").toString();
      const vendorName = (
        row.vendor?.name ||
        row.vendorName ||
        row.vendor_name ||
        ""
      ).toString();
      const amount = (row.grandTotal ?? row.totalAmount ?? row.total ?? row.amount ?? "").toString();

      return (
        invoiceNo.toLowerCase().includes(term) ||
        vendorName.toLowerCase().includes(term) ||
        amount.toLowerCase().includes(term)
      );
    });
  }, [list, search]);

  const totalRecords = filteredList.length;

  return (
    <>
      <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <Toolbar />
        <Box sx={{ maxWidth: 1300, mx: "auto", px: 3, pb: 4, pt: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              <KeyboardArrowLeft
                onClick={() => navigate(-1)}
                fontSize="large"
                sx={{ cursor: "pointer" }}
              />
              <Box
                sx={(theme) => ({
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: theme.palette.primary.light + "22",
                  color: theme.palette.primary.main,
                })}
              >
                <LocalMallOutlined fontSize="medium" />
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="h5" fontWeight={700} color="#003049">
                  Local Procurement List
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View all local procurements created for this outlet. Use filters
                  below to narrow down the records.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 3,
              border: "1px solid #e3e7ed",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(246,249,252,0.9))",
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search (Invoice / Vendor / Amount)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={vendors}
                  getOptionLabel={(option) => option.name || ""}
                  value={vendor}
                  onChange={(e, newVal) => setVendor(newVal)}
                  renderInput={(params) => (
                    <TextField {...params} label="Vendor" placeholder="All" />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={1}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
              >
                <IconButton
                  size="small"
                  onClick={handleReset}
                  sx={{ mr: 1 }}
                  title="Reset filters"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSearchClick}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#003049",
                    "&:hover": { backgroundColor: "#002433" },
                  }}
                >
                  Go
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#f9fafb",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  Procurements
                </Typography>

                <Chip
                  size="small"
                  label={`${totalRecords} record${totalRecords === 1 ? "" : "s"}`}
                  sx={{ fontSize: 11, height: 22 }}
                />
              </Stack>
            </Box>

            {loading ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
                  Loading procurements...
                </Typography>
              </Box>
            ) : list.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  No local procurements found.
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  Try changing the filters or create a new local procurement entry.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}>
                          Sr.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}>
                          Invoice No.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}>
                          Vendor
                        </TableCell>

                        {/* ✅ TAX COLUMNS REMOVED COMPLETELY */}
                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}
                          align="right"
                        >
                          Amount (₹)
                        </TableCell>

                        <TableCell
                          sx={{ fontWeight: 600, backgroundColor: "#f3f4f6" }}
                          align="center"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredList
                        .slice()
                        .reverse()
                        .map((row, index) => {
                          const createdAt = row.createdAt || row.date || row.created_on;
                          const invoiceNo = row.invoiceNo || row.invoice_no || "-";
                          const vendorName =
                            row.vendor?.name || row.vendorName || row.vendor_name || "-";

                          // ✅ amount only (no taxable/tax)
                          const amount = row.totalTaxable ?? 0;

                          return (
                            <TableRow
                              key={row._id || index}
                              hover
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#f8fafc",
                                },
                              }}
                            >
                              <TableCell>{(page - 1) * limit + (index + 1)}</TableCell>
                              <TableCell>{formatDate(createdAt)}</TableCell>
                              <TableCell>{invoiceNo}</TableCell>
                              <TableCell>
                                <Chip label={vendorName} size="small" sx={{ maxWidth: 160 }} />
                              </TableCell>

                              <TableCell align="right">{formatMoney(amount)}</TableCell>

                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewItems(row)}
                                    sx={{
                                      backgroundColor: "#e3f2fd",
                                      "&:hover": { backgroundColor: "#bbdefb" },
                                    }}
                                  >
                                    <VisibilityOutlinedIcon
                                      fontSize="small"
                                      sx={{ color: "#1565c0" }}
                                    />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadInvoice(row)}
                                    sx={{
                                      backgroundColor: "#d1f7d6",
                                      "&:hover": { backgroundColor: "#b2e8b5" },
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    onClick={() => handleAskDeleteProcurement(row)}
                                    sx={{
                                      backgroundColor: "#fdecea",
                                      "&:hover": { backgroundColor: "#f9d6d2" },
                                    }}
                                  >
                                    <DeleteOutlineIcon
                                      fontSize="small"
                                      sx={{ color: "#d32f2f" }}
                                    />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: "1px solid #e0e0e0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: 12,
                    backgroundColor: "#fafbfc",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Showing {startIndex}–{endIndex} of {total || list.length} • Page {page} of{" "}
                    {totalPages}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={page === 1}
                      onClick={() => handlePageChange(-1)}
                      sx={{ textTransform: "none" }}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={page >= totalPages}
                      onClick={() => handlePageChange(1)}
                      sx={{ textTransform: "none" }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Items Dialog (only qty + amount) */}
      <Dialog
        open={itemsDialogOpen}
        onClose={handleCloseItemsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, backgroundColor: "#f7f9fb" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            background: "linear-gradient(90deg, #003049, #274c77)",
            color: "white",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Procurement Items
            </Typography>
            {activeProcurement && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Invoice #{activeProcurement.invoiceNo} • {activeProcurement.vendor?.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseItemsDialog} size="small" sx={{ color: "white" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {itemsLoading ? (
            <Box
              sx={{
                py: 5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <CircularProgress size={26} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading items...
              </Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <Typography>No items found for this procurement.</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 1,
                borderRadius: 2,
                border: "1px solid #e0e6ec",
                overflow: "hidden",
                backgroundColor: "white",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f3f5" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Sr.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Amount (₹)
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item, idx) => {
                    const rmName = item.rawMaterial?.name || item.rawMaterialName || "Unnamed";
                    const amount = computeItemAmount(item);

                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "#fafcff",
                          },
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{rmName}</TableCell>
                        <TableCell align="right">{Number(item.quantity || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{formatMoney(amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleCloseItemsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            backgroundColor: "#fffaf4",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: "#b33a3a",
          }}
        >
          ⚠️ Delete Procurement
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" fontWeight={600}>
            Are you sure you want to delete this record?
          </Typography>

          {deleteTarget && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: "#fff" }}>
              <Typography variant="body2">
                <strong>Invoice:</strong> {deleteTarget.invoiceNo}
              </Typography>
              <Typography variant="body2">
                <strong>Vendor:</strong> {deleteTarget.vendor?.name || "Unknown"}
              </Typography>
            </Box>
          )}

          <Typography variant="caption" sx={{ mt: 1.5, color: "#b33a3a", display: "block" }}>
            This action cannot be undone. All related data will be permanently removed.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteProcurement}
            disabled={deleteLoading}
            sx={{ fontWeight: 600, px: 3 }}
          >
            {deleteLoading ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocalStockListing;
